import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FileText, AlertTriangle, Clock, Calendar, Printer, BookOpen } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import MedicalText from '../components/MedicalText';
import LanguageToggle from '../components/LanguageToggle';
import { afterVisitContent } from '../data/afterVisitContent';
import { findProcedureForAfterVisit } from '../data/procedureAfterVisitLinks';
import { t } from '../utils/translations';

// Spanish translations for after-visit content
const translateAfterVisitContent = (content, language) => {
  if (language === 'en' || !content) return content;
  
  const headingTranslations = {
    'Reason for Visit': 'Razón de la Visita',
    'What to Expect': 'Qué Esperar',
    'What You Should Do': 'Qué Debe Hacer',
    'Activity Guidelines': 'Guías de Actividad',
    'Normal Recovery Symptoms': 'Síntomas Normales de Recuperación',
    'Contact a Urologist Promptly': 'Contacte a un Urólogo Pronto',
    'Seek Emergency Care Immediately If': 'Busque Atención de Emergencia Inmediatamente Si',
    'Follow-Up': 'Seguimiento',
    'Catheter Care': 'Cuidado del Catéter',
    'Incision Care': 'Cuidado de la Incisión',
    'Wound Care': 'Cuidado de la Herida',
    'Medications': 'Medicamentos',
    'Diet': 'Dieta',
    'About This Guide': 'Acerca de Esta Guía',
    'EMERGENCY': 'EMERGENCIA',
    'URGENT': 'URGENTE',
    'ROUTINE': 'RUTINA',
    'When to Call Your Doctor': 'Cuándo Llamar a Su Médico',
    'Warning Signs': 'Señales de Advertencia',
    'Normal Symptoms': 'Síntomas Normales',
    'Hospital Recovery': 'Recuperación en el Hospital',
    'Going Home': 'Regreso a Casa',
    'First 24-48 Hours': 'Primeras 24-48 Horas',
    'First Week': 'Primera Semana',
    'Weeks 2-4': 'Semanas 2-4',
    'Recovery Timeline': 'Cronograma de Recuperación',
    'Safety Precautions': 'Precauciones de Seguridad',
    'Managing Side Effects': 'Manejo de Efectos Secundarios',
    'Stoma Care': 'Cuidado del Estoma',
    'Neobladder Care': 'Cuidado de la Neovejiga',
    'Nephrostomy Tube Care': 'Cuidado del Tubo de Nefrostomía',
    'Ureteral Stent': 'Stent Ureteral',
    'Post-Ablation Syndrome': 'Síndrome Post-Ablación',
    'Pathology Results': 'Resultados de Patología',
    'PSA Monitoring': 'Monitoreo de PSA',
    'Incontinence Management': 'Manejo de la Incontinencia',
    'Erectile Function': 'Función Eréctil',
    'Living with One Testicle': 'Vivir con un Testículo',
    'Passing Stone Fragments': 'Paso de Fragmentos de Cálculos',
    'Treatment Schedule Reminder': 'Recordatorio del Calendario de Tratamiento',
    'Follow-Up and Surveillance': 'Seguimiento y Vigilancia',
    'Ileal Conduit': 'Conducto Ileal',
    'Fever Guidelines': 'Guías sobre la Fiebre',
    'Blood in Urine Guidelines': 'Guías sobre Sangre en la Orina',
    'Pain Guidelines': 'Guías sobre el Dolor',
    'Catheter Problems': 'Problemas con el Catéter',
    'When in Doubt': 'En Caso de Duda'
  };
  
  // Clone content and translate
  const translated = {
    title: translateTitle(content.title, headingTranslations),
    sections: content.sections.map(section => ({
      ...section,
      heading: translateHeading(section.heading, headingTranslations),
      content: section.content ? translateText(section.content) : undefined,
      items: section.items ? section.items.map(item => translateText(item)) : undefined
    }))
  };
  
  return translated;
};

const translateTitle = (title, translations) => {
  // Common title translations
  const titleTranslations = {
    'Post-Ureteroscopy Care': 'Cuidados Post-Ureteroscopia',
    'Post-ESWL (Shockwave Lithotripsy) Care': 'Cuidados Post-ESWL (Litotricia por Ondas de Choque)',
    'Post-PCNL (Percutaneous Nephrolithotomy) Care': 'Cuidados Post-PCNL (Nefrolitotomía Percutánea)',
    'BCG Therapy Aftercare': 'Cuidados Después de la Terapia BCG',
    'Post-TURBT / Bladder Biopsy Care': 'Cuidados Post-TURBT / Biopsia de Vejiga',
    'Post-Radical Cystectomy Care': 'Cuidados Post-Cistectomía Radical',
    'Post-Radical Prostatectomy Care': 'Cuidados Post-Prostatectomía Radical',
    'Post-Orchiectomy Care': 'Cuidados Post-Orquiectomía',
    'Post-Kidney Ablation Care': 'Cuidados Post-Ablación Renal',
    'Warning Signs: When to Seek Care': 'Señales de Advertencia: Cuándo Buscar Atención',
    'Post-Cystoscopy Care': 'Cuidados Post-Cistoscopia',
    'Cystoscopy Aftercare': 'Cuidados Post-Cistoscopia',
    'Ureteral Stent Care': 'Cuidado del Stent Ureteral',
    'Post-Vasectomy Instructions': 'Instrucciones Post-Vasectomía',
    'Vasectomy Aftercare': 'Cuidados Post-Vasectomía',
    'Post-Prostate Biopsy Care': 'Cuidados Post-Biopsia de Próstata',
    'Prostate Biopsy Aftercare': 'Cuidados Post-Biopsia de Próstata',
    'Foley Catheter Care': 'Cuidado del Catéter Foley',
    'General Post-Procedure Care': 'Cuidados Generales Post-Procedimiento',
    'Kidney Stone – Conservative Management': 'Cálculo Renal – Manejo Conservador',
    'Kidney Stone – Post Emergency Department Follow-Up': 'Cálculo Renal – Seguimiento Post-Emergencia',
    'Post-Ureteroscopy / Laser Lithotripsy': 'Post-Ureteroscopia / Litotricia Láser',
    'Elevated PSA – Observation': 'PSA Elevado – Observación',
    'Benign Prostatic Hyperplasia (BPH)': 'Hiperplasia Prostática Benigna (HPB)',
    'Acute Urinary Retention – Catheter Care': 'Retención Urinaria Aguda – Cuidado del Catéter',
    'Catheter Removal Instructions': 'Instrucciones de Retiro del Catéter',
    'Hematuria Evaluation': 'Evaluación de Hematuria',
    'Recurrent Urinary Tract Infections': 'Infecciones Urinarias Recurrentes',
    'Erectile Dysfunction': 'Disfunción Eréctil',
    'Testicular Pain – Non-Emergent': 'Dolor Testicular – No Urgente',
    'Post Prostate Outlet Surgery Instructions': 'Instrucciones Post-Cirugía de Salida Prostática'
  };
  
  return titleTranslations[title] || title;
};

const translateHeading = (heading, translations) => {
  // Check exact match
  if (translations[heading]) return translations[heading];
  
  // Check partial matches
  for (const [en, es] of Object.entries(translations)) {
    if (heading.includes(en)) {
      return heading.replace(en, es);
    }
  }
  
  return heading;
};

const translateText = (text) => {
  if (!text) return text;
  
  let translated = text;
  
  // Common phrase translations
  const phrases = [
    ['Fever', 'Fiebre'],
    ['fever', 'fiebre'],
    ['Blood in urine', 'Sangre en la orina'],
    ['blood in urine', 'sangre en la orina'],
    ['Pain', 'Dolor'],
    ['pain', 'dolor'],
    ['Swelling', 'Hinchazón'],
    ['swelling', 'hinchazón'],
    ['Bleeding', 'Sangrado'],
    ['bleeding', 'sangrado'],
    ['Contact your doctor', 'Contacte a su médico'],
    ['Call your doctor', 'Llame a su médico'],
    ['Seek emergency care', 'Busque atención de emergencia'],
    ['Go to the ER', 'Vaya a la sala de emergencias'],
    ['normal', 'normal'],
    ['expected', 'esperado'],
    ['hours', 'horas'],
    ['days', 'días'],
    ['weeks', 'semanas'],
    ['Rest', 'Descanso'],
    ['Walk', 'Caminar'],
    ['Drink plenty of fluids', 'Beba muchos líquidos'],
    ['No heavy lifting', 'No levante objetos pesados'],
    ['No strenuous activity', 'No realice actividades extenuantes'],
    ['No driving', 'No conduzca'],
    ['Follow-up appointment', 'Cita de seguimiento'],
    ['as directed', 'según las indicaciones'],
    ['as prescribed', 'según lo recetado'],
    ['if prescribed', 'si se lo recetan']
  ];
  
  phrases.forEach(([en, es]) => {
    translated = translated.replace(new RegExp(en, 'g'), es);
  });
  
  return translated;
};

const AfterVisitDetailPage = () => {
  const { instructionId } = useParams();
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [instructionId]);

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  const rawContent = afterVisitContent[instructionId];
  const content = language === 'es' ? translateAfterVisitContent(rawContent, language) : rawContent;
  const relatedProcedure = findProcedureForAfterVisit(instructionId);

  if (!content) {
    return (
      <PageWrapper>
        <div className="empty-state" data-testid="instruction-not-found">
          <FileText size={48} />
          <h2>{language === 'en' ? 'Instructions Not Found' : 'Instrucciones No Encontradas'}</h2>
          <button 
            onClick={() => navigate('/after-visit')} 
            className="btn-primary"
            data-testid="back-to-after-visit-btn"
          >
            {language === 'en' ? 'Back to After Visit' : 'Volver a Cuidados Post-Visita'}
          </button>
        </div>
      </PageWrapper>
    );
  }

  // Helper to get urgency styling class
  const getUrgencyClass = (urgencyLevel) => {
    switch(urgencyLevel) {
      case 'emergency': return 'urgency-level-emergency';
      case 'urgent': return 'urgency-level-urgent';
      case 'routine': return 'urgency-level-routine';
      default: return '';
    }
  };

  // Helper to get urgency icon
  const getUrgencyIcon = (urgencyLevel, section) => {
    switch(urgencyLevel) {
      case 'emergency': return <AlertTriangle size={20} />;
      case 'urgent': return <Clock size={20} />;
      case 'routine': return <Calendar size={20} />;
      default: return section?.warning ? <AlertTriangle size={20} /> : null;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <PageWrapper>
      <div className="after-visit-header-actions">
        <button 
          onClick={() => navigate('/after-visit')} 
          className="back-btn"
          data-testid="back-btn"
        >
          ← {language === 'en' ? 'Back to After Visit' : 'Volver'}
        </button>
        <div className="header-right-actions">
          <LanguageToggle language={language} onToggle={toggleLanguage} />
          <button onClick={handlePrint} className="print-btn" data-testid="print-btn">
            <Printer size={16} />
            {language === 'en' ? 'Print' : 'Imprimir'}
          </button>
        </div>
      </div>

      <article className="instruction-article" data-testid="instruction-article">
        <h1>{content.title}</h1>

        {/* Related Procedure Link */}
        {relatedProcedure && (
          <div className="related-procedure-link" data-testid="related-procedure-link">
            <BookOpen size={18} />
            <span>{language === 'en' ? 'Want to learn more about this procedure?' : '¿Quiere saber más sobre este procedimiento?'}</span>
            <Link to={relatedProcedure.searchPath}>
              {language === 'en' ? `Read about ${relatedProcedure.procedureTitle}` : `Leer sobre ${relatedProcedure.procedureTitle}`}
            </Link>
          </div>
        )}

        {/* Article Image */}
        {rawContent.image && (
          <div className="article-image-container" data-testid="article-image">
            <img 
              src={rawContent.image.url} 
              alt={rawContent.image.alt}
              className="article-main-image"
            />
            {rawContent.image.caption && (
              <figcaption className="article-image-caption">
                <span className="caption-text">{rawContent.image.caption}</span>
                <span className="caption-source">{rawContent.image.source}</span>
              </figcaption>
            )}
          </div>
        )}

        {content.sections.map((section, index) => (
          <div 
            key={index} 
            className={`instruction-section ${section.warning ? 'warning-section' : ''} ${getUrgencyClass(section.urgencyLevel)}`}
            data-testid={`section-${index}`}
          >
            <h2>
              {section.urgencyLevel ? getUrgencyIcon(section.urgencyLevel, section) : (section.warning && <AlertTriangle size={20} />)}
              {' '}{section.heading}
            </h2>
            {section.content && <p><MedicalText>{section.content}</MedicalText></p>}
            {section.items && (
              <ul>
                {section.items.map((item, i) => (
                  <li key={i}><MedicalText>{item}</MedicalText></li>
                ))}
              </ul>
            )}
          </div>
        ))}

        <div className="article-disclaimer" data-testid="article-disclaimer">
          <AlertTriangle size={16} />
          <p>
            <strong>{language === 'en' ? 'Important:' : 'Importante:'}</strong> {t('disclaimerText', language)}
          </p>
        </div>
      </article>
    </PageWrapper>
  );
};

export default AfterVisitDetailPage;
